import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { MergeApiClient } from 'api/merge';
import { MergeModel, MergeSummaryModel, PaginationModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ProcessStatus } from 'models/types';

export function useMerges() {
  return useQuery<MergeSummaryModel[]>({
    queryKey: ['merges'],
    queryFn: MergeApiClient.getAllMerges
  });
}

export function useMergesByStatus(status: ProcessStatus) {
  return useQuery<MergeSummaryModel[]>({
    queryKey: ['merges', 'status', status],
    queryFn: () => MergeApiClient.getMergesByStatus(status),
    enabled: !!status
  });
}

export function useMergeDetails(id: string) {
  return useQuery<MergeModel>({
    queryKey: ['merge-details', id],
    queryFn: () => MergeApiClient.getMergeById(id),
    enabled: !!id
  });
}

export function useDetailedStatusMerges(status: ProcessStatus) {
  return useQuery<PaginationModel<MergeModel>>({
    queryKey: ['merges-detailed-status', status],
    queryFn: () => MergeApiClient.getDetailedStatusMerges(status),
    enabled: !!status
  });
}

export function useCreateMerge() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (payload: JSONObject) => MergeApiClient.createMerge(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merges'] });
      queryClient.invalidateQueries({ queryKey: ['merges-detailed-status'] });
      toast({
        title: 'Merge created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create merge',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useApproveMerge() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (mergeId: string) => MergeApiClient.approveMerge(mergeId),
    onSuccess: (mergeId) => {
      queryClient.invalidateQueries({ queryKey: ['merge-details', mergeId] });
      queryClient.invalidateQueries({ queryKey: ['merges'] });
      queryClient.invalidateQueries({ queryKey: ['merges-detailed-status'] });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return Array.isArray(key) && key[0] === 'artists' && key[2] === 'items';
        }
      });
      toast({
        title: 'Merge approved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to approve merge',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useRejectMerge() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (mergeId: string) => MergeApiClient.rejectMerge(mergeId),
    onSuccess: (mergeId) => {
      queryClient.invalidateQueries({ queryKey: ['merge-details', mergeId] });
      queryClient.invalidateQueries({ queryKey: ['merges'] });
      queryClient.invalidateQueries({ queryKey: ['merges-detailed-status'] });
      toast({
        title: 'Merge rejected successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reject merge',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

// Bulk operations - these don't invalidate/toast on each item, only once at the end
export function useBulkApproveMerges() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (mergeIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        mergeIds.map(id => MergeApiClient.approveMerge(id))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['merges'] });
      queryClient.invalidateQueries({ queryKey: ['merges-detailed-status'] });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return Array.isArray(key) && key[0] === 'artists' && key[2] === 'items';
        }
      });

      toast({
        title: `Bulk approve completed`,
        description: `${succeeded} succeeded${failed ? `, ${failed} failed` : ''}`,
        status: failed ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk approve failed',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}

export function useBulkRejectMerges() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (mergeIds: string[]): Promise<{ succeeded: number; failed: number }> => {
      const results = await Promise.allSettled(
        mergeIds.map(id => MergeApiClient.rejectMerge(id))
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      queryClient.invalidateQueries({ queryKey: ['merges'] });
      queryClient.invalidateQueries({ queryKey: ['merges-detailed-status'] });

      toast({
        title: `Bulk reject completed`,
        description: `${succeeded} succeeded${failed ? `, ${failed} failed` : ''}`,
        status: failed ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk reject failed',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  });
}
